// ./src/pages/private/UserInvitationsPage/UserInvitationsPage.tsx
import React, { useState, useCallback } from "react";
import { Stack } from "@mantine/core";
import { PageProps } from "@sdflc/react-router-pages";

import { DashboardLayout } from "@/components/layouts";
import {
  AppButton,
  AppToolbar,
  ConfirmModal,
  EmptyState,
  SearchInput,
} from "@/components/generic";
import {
  UserInvitationFormModal,
  UserInvitationsTable,
} from "@/components/features";
import { useUserInvitations } from "@/hooks";
import { UserInvitation } from "@/utils/api";
import { useApp } from "@/contexts";
import { DEFAULT_PAGE_SIZE } from "@/config";

export const UserInvitationsPage: React.FC<PageProps> = (props: PageProps) => {
  const { isAuthenticated } = useApp();

  // Use the user invitations hook for all data management
  const {
    // Data state
    userInvitations,
    loading,
    loadingMore,
    hasMore,
    isEmpty,

    // Search and filters
    sortBy,

    // Operations state
    isCreating,
    isUpdating,
    removingInvitationId,
    resendingInvitationId,
    selectedUserInvitation,

    // Data operations
    list,
    loadMore,
    refresh,

    // Search and filter operations
    setFilters,
    setSortBy,

    // CRUD operations
    create,
    update,
    remove,
    resend,

    // UI state management
    setSelectedUserInvitation,

    // Error handling
    lastError,
    clearError,
  } = useUserInvitations({
    autoLoad: true,
    isAuthenticated,
    pageSize: 20,
  });

  // Local UI state
  const [formModalOpened, setFormModalOpened] = useState<boolean>(false);
  const [invitationToDelete, setInvitationToDelete] =
    useState<UserInvitation | null>(null);
  const [searchValue, setSearchValue] = useState<string>("");

  // Handle search input change
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);

      // Apply search filter
      const newFilters: any = {};
      if (value.trim()) {
        newFilters.keyword = value.trim();
        newFilters.searchIn = ["firstName", "lastName", "email"];
      }

      // Update filters and reload data
      setFilters(newFilters);
      list(newFilters, { page: 1, pageSize: DEFAULT_PAGE_SIZE }, sortBy, true);
    },
    [sortBy, setFilters, list]
  );

  // Handle add invitation button click
  const handleAddInvitation = useCallback(() => {
    setSelectedUserInvitation(null);
    setFormModalOpened(true);
  }, [setSelectedUserInvitation]);

  // Handle edit invitation
  const handleEditInvitation = useCallback(
    (invitation: UserInvitation) => {
      setSelectedUserInvitation(invitation);
      setFormModalOpened(true);
    },
    [setSelectedUserInvitation]
  );

  // Handle remove invitation button click
  const handleRemoveInvitationClick = useCallback(
    (invitation: UserInvitation) => {
      setInvitationToDelete(invitation);
    },
    []
  );

  // Handle resend invitation
  const handleResendInvitation = useCallback(
    async (invitation: UserInvitation) => {
      if (invitation.id) {
        await resend(invitation.id);
      }
    },
    [resend]
  );

  // Handle confirm delete
  const handleConfirmDelete = useCallback(async () => {
    if (invitationToDelete?.id) {
      const success = await remove(invitationToDelete.id);
      if (success) {
        setInvitationToDelete(null);
      }
    }
  }, [invitationToDelete, remove]);

  // Handle cancel delete
  const handleCancelDelete = useCallback(() => {
    setInvitationToDelete(null);
  }, []);

  // Handle form modal close
  const handleFormModalClose = useCallback(() => {
    setFormModalOpened(false);
    setSelectedUserInvitation(null);
    clearError();
  }, [setSelectedUserInvitation, clearError]);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    refresh();
  }, [refresh]);

  // Handle invitation creation
  const handleCreateInvitation = useCallback(
    async (invitationData: any) => {
      const result = await create(invitationData);
      if (result) {
        setFormModalOpened(false);
        setSelectedUserInvitation(null);
      }
      return result;
    },
    [create, setSelectedUserInvitation]
  );

  // Handle invitation update
  const handleUpdateInvitation = useCallback(
    async (invitationId: string, invitationData: any) => {
      const result = await update(invitationId, invitationData);
      if (result) {
        setFormModalOpened(false);
        setSelectedUserInvitation(null);
      }
      return result;
    },
    [update, setSelectedUserInvitation]
  );

  return (
    <DashboardLayout {...props}>
      <Stack gap="md">
        {/* Page toolbar with actions and search */}
        <AppToolbar
          preset="page-header"
          sections={[
            {
              id: "actions",
              items: [
                <AppButton
                  key="add-invitation"
                  iconName="Plus"
                  onClick={handleAddInvitation}
                  disabled={isCreating || !!removingInvitationId || loading}
                >
                  Invite User
                </AppButton>,
                <AppButton
                  key="refresh"
                  variant="secondary"
                  iconName="Refresh"
                  onClick={handleRefresh}
                  disabled={isCreating || !!removingInvitationId || loading}
                >
                  Refresh
                </AppButton>,
              ],
              align: "left",
              gap: "sm",
            },
            {
              id: "search",
              items: [
                <SearchInput
                  key="search"
                  placeholder="Search invitations..."
                  value={searchValue}
                  onChange={(value) => handleSearchChange(value)}
                  style={{ minWidth: 250 }}
                />,
              ],
              align: "right",
            },
          ]}
        />

        {/* User invitations table */}
        <UserInvitationsTable
          userInvitations={userInvitations}
          loading={loading}
          loadingMore={loadingMore}
          hasMore={hasMore}
          sortBy={sortBy}
          onSortChange={setSortBy}
          onLoadMore={loadMore}
          onEditInvitation={handleEditInvitation}
          onRemoveInvitation={handleRemoveInvitationClick}
          onResendInvitation={handleResendInvitation}
          removingInvitationId={removingInvitationId}
          resendingInvitationId={resendingInvitationId}
        />

        {/* Empty state when no invitations */}
        <EmptyState
          display={isEmpty && !loading}
          title="No user invitations yet"
          description="Start by inviting your first user to join the platform"
          actionIcon="Plus"
          actionLabel="Invite User"
          onAction={handleAddInvitation}
        />
      </Stack>

      {/* User invitation form modal for create/edit */}
      <UserInvitationFormModal
        opened={formModalOpened}
        onClose={handleFormModalClose}
        userInvitation={selectedUserInvitation}
        onCreateInvitation={handleCreateInvitation}
        onUpdateInvitation={handleUpdateInvitation}
        isCreating={isCreating}
        isUpdating={isUpdating}
        lastError={lastError}
        onClearError={clearError}
      />

      {/* Delete confirmation modal */}
      <ConfirmModal
        opened={!!invitationToDelete}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete User Invitation"
        message={`Are you sure you want to delete the invitation for "${invitationToDelete?.email}"?`}
        details="This action cannot be undone. The invitation will be permanently removed and the user will not be able to accept it."
        confirmInProgress={removingInvitationId === invitationToDelete?.id}
        confirmLabel="Delete"
        type="danger"
      />
    </DashboardLayout>
  );
};
